// src/components/credits/CustomerBillingForm.jsx — the customer's billing record,
// readable and editable by a customer-admin (or a superadmin) since 2026-08-24.
//
// ⚠️ Mounted in THREE places: /billing-details (avatar menu, company resolved from
// the ACTIVE workspace) and the "Dati azienda" tab of Gestione Workspace (company
// from the console selector) — both through CompanyBillingBody.jsx — plus the
// superadmin TenantCreditsSection directly (with canEditCountry, and an `onSaved`
// that clears its 409 checklist). Not mounted on /credits.
//
// The missing-fields list flags the matching inputs; `sdi_code_or_pec_email`
// flags BOTH sdi_code and pec_email (either one satisfies it).
//
// Turns the CUSTOMER_NOT_INVOICEABLE checklist into something the company can
// actually fix: GET /admin/customers/{id} returns the DERIVED
// `missing_billing_fields[]` (the same list the 409 carries as `missing[]`), so
// the form shows what blocks a paid plan BEFORE anyone tries to assign one, and
// PATCH writes exactly those fields.
//
// Rules this component enforces (see adminApi.adminGetCustomer/UpdateCustomer):
//   • 403 CUSTOMER_ADMIN_REQUIRED on load → render NOTHING. That code is also what
//     an UNKNOWN id gets (the gate precedes the lookup), so it must never read as
//     "not found". Being admin of a different customer, or a `viewer`, lands here.
//   • 403 on save → the claims went stale mid-session; show the re-auth line
//     (apiFetch has already raised the permissions-drift banner).
//   • `country_code` is sent ONLY when canEditCountry (superadmin). For anyone
//     else the BE answers 422 CUSTOMER_INVALID — refused, never stripped — so the
//     input is not even rendered for them.
//   • PATCH semantics: only CHANGED keys are sent; an emptied nullable field is
//     sent as null (clear); legal_name / vat_number / tech_email are NOT NULL and are refused
//     client-side when blanked.
//   • `missing[] === ['customer']` never reaches this component: there is no
//     customer id to load, and the caller renders the operator-fix copy instead.
//   • ⛔⛔ Every input carries the COLUMN's length (MAX_LENGTH) and the province
//     must be a 2-letter code. The PATCH model has NO length limits of its own, so
//     an over-long value reaches the database, fails there, and comes back as an
//     unhandled 500 WITHOUT CORS headers — which the browser reports as a bare
//     network failure ("NetworkError when attempting to fetch resource", seen
//     2026-09-26 with "modena" in a CHAR(2) province). Fixed backend-side in dbd8a73
//     (the same limits enforced there → 422 CUSTOMER_INVALID naming the field); these
//     `maxLength`s stay so a user rarely meets that refusal, as the guide asks.
//   • `embedded` (the "Dati azienda" tab): the form sits INSIDE a card its caller
//     draws, so it drops its own card chrome and its own title (the caller's card
//     header carries them) and takes the Gestione Workspace control sizes — 46px
//     fields, 11px upper-case labels, the 46px accent button. The other two mounts
//     pass nothing and render exactly as before.
//   • ⛔ No error on this form is `err.message`: a transport failure is a
//     TypeError whose message is the BROWSER's English, and the server's own
//     sentences are not ours to show. Worded by code → `billing.errors.*`.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Square, CheckSquare } from 'lucide-react';
import { adminGetCustomer, adminUpdateCustomer } from '../../admin/adminApi.js';
import { COLORS, FONT_SANS, FONT_DISPLAY, RADII, SEMANTIC } from '../../constants/theme.js';

// Editable columns, in render order, grouped by fieldset. `country_code` is
// appended to identity only when canEditCountry.
const GROUPS = [
  { legend: 'identity', fields: ['legal_name', 'vat_number', 'tax_code'] },
  { legend: 'invoice', fields: ['sdi_code', 'pec_email'] },
  { legend: 'address', fields: ['billing_address', 'billing_city', 'billing_zip', 'billing_province'] },
  { legend: 'contacts', fields: ['tech_email', 'billing_email'] },
];
const NOT_NULL = new Set(['legal_name', 'tech_email', 'vat_number']);
// The `customers` column sizes (backend src/auth/db.py). ⚠️ Mirror them exactly —
// see the header: the API does not enforce them, the database does, by crashing.
const MAX_LENGTH = {
  legal_name: 200, vat_number: 20, tax_code: 20, sdi_code: 7, pec_email: 255,
  billing_address: 200, billing_city: 100, billing_zip: 10, billing_province: 2,
  tech_email: 255, billing_email: 255,
};
// Italian province code (CHAR(2)). Checked before the PATCH, not only capped:
// "M" or "M1" would fit the column and still be wrong on an invoice.
const PROVINCE_RE = /^[A-Z]{2}$/;
const EMAIL_FIELDS = new Set(['pec_email', 'tech_email', 'billing_email']);

// A `missing[]` entry → the inputs it flags. The BE's composite entry maps to
// both of its alternatives; every other entry is a column name.
function flaggedInputs(missing) {
  const set = new Set();
  (missing || []).forEach((m) => {
    if (m === 'sdi_code_or_pec_email') { set.add('sdi_code'); set.add('pec_email'); } else set.add(m);
  });
  return set;
}

const cardStyle = {
  background: COLORS.surface, borderRadius: RADII.control,
  border: `1px solid ${COLORS.border}`, padding: '20px 22px', fontFamily: FONT_SANS, color: COLORS.ink,
};
const inputStyle = (flagged) => ({
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '13px', fontFamily: FONT_SANS,
  borderRadius: RADII.controlInner,
  border: `1px solid ${flagged ? SEMANTIC.attenzione.fg : COLORS.borderStrong}`,
  background: flagged ? SEMANTIC.attenzione.bg : COLORS.surface,
});
const labelStyle = { display: 'block', fontSize: '12px', color: COLORS.textSecondary, marginBottom: '4px' };
// The Gestione Workspace control sizes (CompanyAdminsSection / CompanyWorkspacesPage).
const embeddedInputStyle = (flagged) => ({
  ...inputStyle(flagged), height: '46px', padding: '0 14px', fontSize: '13px', borderRadius: RADII.control,
  border: `1px solid ${flagged ? SEMANTIC.attenzione.fg : COLORS.border}`, outline: 'none',
});
const embeddedLabelStyle = {
  display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px',
  textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: '8px',
};

const legendStyle = { fontSize: '12px', fontWeight: 700, color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em', padding: 0, marginBottom: '8px' };
const embeddedLegendStyle = {
  fontFamily: FONT_DISPLAY, fontSize: '13px', fontWeight: 700, color: COLORS.ink, padding: 0, marginBottom: '12px',
};
const fieldsetStyle = { border: 'none', padding: 0, margin: '0 0 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' };

const asForm = (customer, fields) => Object.fromEntries(fields.map((f) => [f, customer?.[f] ?? '']));

// A refused or failed request → OUR sentence. A thrown error with no HTTP status
// never reached a readable response: a real network failure. ⭐ Since BE dbd8a73
// (2026-09-27) an unexpected server error is a CORS-safe JSON 500
// `INTERNAL_ERROR` carrying `request_id` (also the X-Request-Id header): we show
// that id for the user to quote — ⛔ never the server's message — and a value
// that does not fit its column is a 422 (`CUSTOMER_INVALID`, or the generic
// `INVALID_INPUT` backstop), never a 500 any more.
function saveErrorCopy(t, err) {
  if (err?.code === 'CUSTOMER_ADMIN_REQUIRED' || err?.status === 403) return t('billing.forbidden');
  if (!err?.status) return t('billing.errors.network');
  if (err.code === 'CUSTOMER_VAT_EXISTS') return t('billing.errors.vatExists');
  if (err.code === 'CUSTOMER_INVALID' || err.status === 422) return t('billing.errors.invalid');
  if (err.status >= 500 && err.requestId) return t('billing.errors.server', { requestId: err.requestId });
  return t('billing.errors.generic', { code: err.code || err.status });
}
function loadErrorCopy(t, err) {
  if (!err?.status) return t('billing.errors.loadNetwork');
  if (err.status >= 500 && err.requestId) return t('billing.errors.loadServer', { requestId: err.requestId });
  return t('billing.errors.loadGeneric', { code: err.code || err.status });
}

export default function CustomerBillingForm({ customerId, canEditCountry = false, onSaved, embedded = false }) {
  // ⭐ 2026-09-24: this form's copy moved from `credits` to `admin` when the
  // form moved off /credits to its own page. It is the company's invoicing
  // identity, not wallet content — the namespace follows the surface.
  const { t } = useTranslation('admin');
  const fields = useMemo(() => {
    const all = GROUPS.flatMap((g) => g.fields);
    return canEditCountry ? [...all, 'country_code'] : all;
  }, [canEditCountry]);

  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveError, setSaveError] = useState('');

  const load = useCallback(async () => {
    if (!customerId) { setLoading(false); return; }
    setLoading(true);
    setLoadError('');
    setHidden(false);
    try {
      const data = await adminGetCustomer(customerId);
      setCustomer(data);
      setForm(asForm(data, fields));
    } catch (err) {
      // "Not for this user" (or an id we may not even probe) — no form at all.
      if (err.code === 'CUSTOMER_ADMIN_REQUIRED' || err.status === 403) setHidden(true);
      else setLoadError(loadErrorCopy(t, err));
    } finally {
      setLoading(false);
    }
  }, [customerId, fields, t]);

  useEffect(() => { load(); }, [load]);

  if (!customerId || hidden) return null;

  const missing = customer?.missing_billing_fields || [];
  const flagged = flaggedInputs(missing);

  function diff() {
    const patch = {};
    for (const f of fields) {
      const next = String(form[f] ?? '').trim();
      const prev = customer?.[f] ?? '';
      if (next === String(prev ?? '')) continue;
      patch[f] = next === '' ? null : next;
    }
    return patch;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaveMsg('');
    setSaveError('');
    const patch = diff();
    if (Object.keys(patch).length === 0) { setSaveMsg(t('billing.nothingToSave')); return; }
    for (const f of Object.keys(patch)) {
      if (NOT_NULL.has(f) && patch[f] === null) {
        setSaveError(t('billing.requiredEmpty', { field: t(`billing.fields.${f}`) }));
        return;
      }
    }
    if (typeof patch.billing_province === 'string' && !PROVINCE_RE.test(patch.billing_province)) {
      setSaveError(t('billing.provinceInvalid'));
      return;
    }
    setSaving(true);
    try {
      const updated = await adminUpdateCustomer(customerId, patch, { allowCountryCode: canEditCountry });
      setCustomer(updated);
      setForm(asForm(updated, fields));
      setSaveMsg(t('billing.saved'));
      onSaved?.(updated);
    } catch (err) {
      // Branch on the code, never the message.
      setSaveError(saveErrorCopy(t, err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={embedded ? { fontFamily: FONT_SANS, color: COLORS.ink } : cardStyle}
      aria-label={t('billing.title')}
    >
      {/* Embedded: the caller's card header carries the title and this intro. */}
      {!embedded && (
        <>
          <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 6px' }}>{t('billing.title')}</h3>
          <p style={{ fontSize: '12px', color: COLORS.textSecondary, margin: '0 0 12px' }}>{t('billing.intro')}</p>
        </>
      )}

      {loading && <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{t('billing.loading')}</div>}
      {loadError && <div role="alert" style={{ fontSize: '12px', color: SEMANTIC.errore.fg }}>{loadError}</div>}

      {customer && (
        <>
          {missing.length === 0 ? (
            <p style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: SEMANTIC.verde.fg, margin: '0 0 14px' }}>
              <CheckSquare size={14} aria-hidden="true" /> {t('billing.complete')}
            </p>
          ) : (
            <div role="status" style={{ fontSize: '13px', color: SEMANTIC.attenzione.fg, margin: '0 0 14px' }}>
              <p style={{ margin: '0 0 6px' }}>{t('billing.incomplete', { count: missing.length })}</p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {missing.map((m) => (
                  <li key={m} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Square size={13} aria-hidden="true" />
                    {/* Unknown entries pass through verbatim: the BE may add one first. */}
                    <span>{t(`billing.missingLabels.${m}`, { defaultValue: m })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {GROUPS.map((g) => (
            <fieldset key={g.legend} style={fieldsetStyle}>
              <legend style={embedded ? embeddedLegendStyle : legendStyle}>{t(`billing.legend.${g.legend}`)}</legend>
              {g.legend === 'invoice' && (
                <p style={{ gridColumn: '1 / -1', fontSize: '11px', color: COLORS.textSecondary, margin: 0 }}>{t('billing.sdiOrPec')}</p>
              )}
              {g.fields.map((f) => (
                <div key={f}>
                  <label htmlFor={`billing-${f}`} style={embedded ? embeddedLabelStyle : labelStyle}>{t(`billing.fields.${f}`)}</label>
                  <input
                    id={`billing-${f}`}
                    type={EMAIL_FIELDS.has(f) ? 'email' : 'text'}
                    value={form[f] ?? ''}
                    maxLength={MAX_LENGTH[f]}
                    onChange={(e) => {
                      // The province is a code: upper-cased as typed, like country_code.
                      const v = f === 'billing_province' ? e.target.value.toUpperCase() : e.target.value;
                      setForm((prev) => ({ ...prev, [f]: v }));
                    }}
                    aria-invalid={flagged.has(f) || undefined}
                    aria-describedby={f === 'billing_province' ? 'billing-province-hint' : undefined}
                    placeholder={f === 'billing_province' ? 'MO' : undefined}
                    style={embedded ? embeddedInputStyle(flagged.has(f)) : inputStyle(flagged.has(f))}
                  />
                  {f === 'billing_province' && (
                    <span id="billing-province-hint" style={{ fontSize: '11px', color: COLORS.textSecondary }}>
                      {t('billing.provinceHint')}
                    </span>
                  )}
                </div>
              ))}
              {g.legend === 'identity' && canEditCountry && (
                <div>
                  <label htmlFor="billing-country_code" style={labelStyle}>{t('billing.fields.country_code')}</label>
                  <input
                    id="billing-country_code" type="text" maxLength={2}
                    value={form.country_code ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, country_code: e.target.value.toUpperCase() }))}
                    style={inputStyle(false)}
                  />
                  <span style={{ fontSize: '11px', color: COLORS.textSecondary }}>{t('billing.countryNote')}</span>
                </div>
              )}
            </fieldset>
          ))}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button type="submit" disabled={saving} style={{
              ...(embedded
                ? { height: '46px', padding: '0 20px', borderRadius: RADII.control }
                : { padding: '9px 16px', borderRadius: RADII.controlInner }),
              border: 'none',
              background: saving ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
              fontSize: '13px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS,
            }}>
              {saving ? t('billing.saving') : t('billing.save')}
            </button>
            {saveMsg && <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{saveMsg}</span>}
            {saveError && <span role="alert" style={{ fontSize: '12px', color: SEMANTIC.errore.fg }}>{saveError}</span>}
          </div>
        </>
      )}
    </form>
  );
}
